import { l } from '@manychat/manyui'

import { IQuickReplyBlock } from 'common/builder/blocks/blockInterfaces'
import { getInstagramNodeNextStep } from 'common/builder/components/chart/FlowChart/adapters/instagram/nodes/converterRules/utils/getInstagramNodeNextStep'
import { getInstagramNodeRightIcon } from 'common/builder/components/chart/FlowChart/adapters/instagram/nodes/converterRules/utils/getInstagramNodeRightIcon'
import { FlowChartContentNodeBuilder } from 'common/builder/components/chart/FlowChart/lib/builders/FlowChartContentNodeBuilder'
import { FlowChartContentNodeHeaderBuilder } from 'common/builder/components/chart/FlowChart/lib/builders/FlowChartContentNodeHeaderBuilder'
import { FlowChartContentRespontentsBuilder } from 'common/builder/components/chart/FlowChart/lib/builders/FlowChartContentRespontentsBuilder'
import { FlowChartEmptyNodeBlockBuilder } from 'common/builder/components/chart/FlowChart/lib/builders/FlowChartEmptyNodeBlockBuilder'
import { FlowChartIconBuilder } from 'common/builder/components/chart/FlowChart/lib/builders/FlowChartIconBuilder'
import {
  ConverterRule,
  NodeConverterRulePayload,
} from 'common/builder/components/chart/FlowChart/lib/types'
import { getContentNodeBlocksList } from 'common/builder/components/chart/FlowChart/lib/utils/getContentNodeBlocksList'
import { getContentNodeStatTable } from 'common/builder/components/chart/FlowChart/lib/utils/getContentNodeStatTable'
import { hasContentBlocks } from 'common/builder/components/chart/FlowChart/lib/utils/hasContentBlocks'
import {
  DEFAULT_ICON_RENDER_SIZE,
  InstagramConfig,
} from 'common/builder/components/chart/FlowChart/nodes/shared/config'
import { FlowChartBlockType } from 'common/builder/components/chart/FlowChart/types/FlowChartBlock'
import { FlowChartContentNode } from 'common/builder/components/chart/FlowChart/types/FlowChartNode/FlowChartContentNode'
import { NodeType } from 'common/builder/constants/NodeType'
import { getNodeCtrUniqueUsers } from 'common/builder/models/Button/typedHelpers'
import { IInstagramNode, INode } from 'common/builder/nodes/nodeInterfaces'

export const ContentNodeInstagramConverterRule: ConverterRule<
  IInstagramNode,
  FlowChartContentNode,
  NodeConverterRulePayload
> = {
  predicate: (entity: INode) => entity?.nodeType === NodeType.INSTAGRAM,

  convert: (
    entity: IInstagramNode,
    { isStatByJSONAdsTrigger, rootNodeId, flowChartBlocks, blocks: iBlocksMap },
  ): FlowChartContentNode | null => {
    const isRootWithJSONAds = rootNodeId === entity.id && isStatByJSONAdsTrigger

    const blocks = getContentNodeBlocksList({
      blockIds: entity.blocks || [],
      blocksMap: flowChartBlocks,
    })

    const quickReplyBlock = blocks.find((block) => block?.type === FlowChartBlockType.QUICK_REPLIES)
    const userInputBlock = blocks.find((block) => block?.type === FlowChartBlockType.USER_INPUT)

    const nextStep = getInstagramNodeNextStep({
      node: entity,
      quickReplyBlock: quickReplyBlock
        ? (iBlocksMap[quickReplyBlock?.id] as IQuickReplyBlock)
        : undefined,
    })

    const respondentsCount = entity.stats?.filledAnswersCount || 0
    const header = new FlowChartContentNodeHeaderBuilder(
      entity.caption,
      new FlowChartIconBuilder(InstagramConfig.iconComponent, DEFAULT_ICON_RENDER_SIZE).build(),
    )
      .withSubtitle(InstagramConfig.preheader || null)
      .withRightIcon(getInstagramNodeRightIcon(entity))
      .build()

    let contentNodeBlocks = blocks

    if (!hasContentBlocks(blocks)) {
      contentNodeBlocks = [
        new FlowChartEmptyNodeBlockBuilder()
          .withText(l.getString(InstagramConfig.placeholderText))
          .build(),
      ]
    }

    return new FlowChartContentNodeBuilder(entity.id)
      .withNodeType(NodeType.INSTAGRAM)
      .withOnboardingElementId(entity.onboardingElementId)
      .withHeader(header)
      .withRespondents(
        userInputBlock
          ? new FlowChartContentRespontentsBuilder(respondentsCount)
              .withContentId(entity.contentId)
              .build()
          : null,
      )
      .withDeliveredUniqueUsers(getNodeCtrUniqueUsers(entity))
      .withStatsTable(getContentNodeStatTable(entity, { isRootWithJSONAds }))
      .withBlocks(contentNodeBlocks)
      .withNextSteps(nextStep ? [nextStep] : [])
      .build()
  },
}
